import React, { useState, useEffect } from "react";
import { safeFetch } from "../utils/api";

const AdminKTCMappingsPage = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [customInputs, setCustomInputs] = useState({});
    const [error, setError] = useState(null);

    const load = () => {
        setLoading(true);
        setError(null);
        safeFetch("/api/admin/ktc-mappings/")
            .then((d) => setData(d))
            .catch((e) => setError(e.message))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
    }, []);

    const saveMapping = (playerName, ktcName) => {
        safeFetch("/api/admin/ktc-mappings/", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ player_name: playerName, ktc_name: ktcName }),
        })
            .then(load)
            .catch((e) => alert("Error saving: " + e.message));
    };

    const deleteAlias = (id) => {
        safeFetch("/api/admin/ktc-mappings/", {
            method: "DELETE",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id }),
        })
            .then(load)
            .catch((e) => alert("Error deleting: " + e.message));
    };

    const dismiss = (playerId) => {
        safeFetch(`/api/admin/ktc-unmatched/${playerId}/dismiss/`, {
            method: "DELETE",
        })
            .then(load)
            .catch((e) => alert("Error dismissing: " + e.message));
    };

    const setInput = (playerName, value) => {
        setCustomInputs((prev) => ({ ...prev, [playerName]: value }));
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-surface flex items-center justify-center">
                <p className="text-text-muted">Loading KTC mappings…</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-surface flex items-center justify-center">
                <p className="text-red-500">Error: {error}</p>
            </div>
        );
    }

    const { unmatched = [], aliases = [] } = data || {};

    return (
        <div className="min-h-screen overflow-x-hidden bg-surface px-3 py-6 sm:px-4 sm:py-8">
            <div className="mx-auto max-w-5xl min-w-0">
                <h1 className="mb-8 break-words text-2xl font-bold text-forest">
                    KTC Player Mappings (Admin)
                </h1>

                {/* Unmatched Players */}
                <section className="mb-10">
                    <h2 className="text-lg font-semibold text-text mb-4 border-b border-border pb-2">
                        Unmatched Players{" "}
                        <span className="text-text-muted font-normal text-sm">
                            ({unmatched.length})
                        </span>
                    </h2>

                    {unmatched.length === 0 ? (
                        <p className="text-text-muted text-sm italic">
                            No unmatched players — great job! 🎉
                        </p>
                    ) : (
                        <div className="space-y-3">
                            {unmatched.map((u) => {
                                const inputVal =
                                    customInputs[u.player_name] !== undefined
                                        ? customInputs[u.player_name]
                                        : "";
                                return (
                                    <div
                                        key={u.id}
                                        className="min-w-0 rounded-lg border border-border bg-white p-4"
                                    >
                                        <div className="flex min-w-0 flex-col gap-4 lg:flex-row lg:items-start">
                                            {/* Player info */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex min-w-0 flex-wrap items-center gap-2">
                                                    <p className="break-words font-medium text-text">
                                                        {u.player_name}
                                                    </p>
                                                    {u.position && (
                                                        <span
                                                            className="inline-flex flex-shrink-0 items-center rounded-full border border-green-200 bg-green-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-green-800"
                                                            title="Position inferred from the best potential KTC match"
                                                        >
                                                            {u.position}
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-xs text-text-muted mt-0.5">
                                                    Seen {u.times_seen}×
                                                </p>

                                                {/* Suggestions */}
                                                {u.suggestions && u.suggestions.length > 0 && (
                                                    <div className="mt-3">
                                                        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
                                                            Potential matches
                                                        </p>
                                                        <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:flex-wrap">
                                                            {u.suggestions.map((s) => {
                                                                const suggestionName =
                                                                    typeof s === "string" ? s : s.name;
                                                                const suggestionPosition =
                                                                    typeof s === "string" ? null : s.position;
                                                                return (
                                                                    <button
                                                                        key={`${suggestionName}-${suggestionPosition || ""}`}
                                                                        onClick={() =>
                                                                            setInput(u.player_name, suggestionName)
                                                                        }
                                                                        className={`min-w-0 rounded border px-3 py-2 text-left text-xs transition-colors sm:rounded-full sm:py-1 ${
                                                                            inputVal === suggestionName
                                                                                ? "bg-forest text-white border-forest"
                                                                                : "bg-green-50 text-green-800 border-green-300 hover:bg-green-100"
                                                                        }`}
                                                                    >
                                                                        <span className="flex min-w-0 items-center gap-2">
                                                                            <span className="min-w-0 break-words">
                                                                                {suggestionName}
                                                                            </span>
                                                                            {suggestionPosition && (
                                                                                <span className="flex-shrink-0 rounded-full bg-white/70 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-green-800">
                                                                                    {suggestionPosition}
                                                                                </span>
                                                                            )}
                                                                        </span>
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Input + actions */}
                                            <div className="flex w-full min-w-0 flex-col gap-2 lg:w-auto lg:flex-row lg:items-center lg:flex-shrink-0">
                                                <input
                                                    type="text"
                                                    value={inputVal}
                                                    onChange={(e) =>
                                                        setInput(u.player_name, e.target.value)
                                                    }
                                                    placeholder="KTC name (or leave blank to ignore)"
                                                    className="w-full min-w-0 rounded border border-border px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-forest lg:w-64 lg:py-1"
                                                />
                                                <div className="flex w-full gap-2 lg:w-auto lg:flex-shrink-0">
                                                    <button
                                                        onClick={() =>
                                                            saveMapping(u.player_name, inputVal)
                                                        }
                                                        className="flex-1 rounded bg-forest px-3 py-2 text-sm text-white transition-colors hover:bg-forest/90 lg:flex-none lg:py-1"
                                                    >
                                                        Save
                                                    </button>
                                                    <button
                                                        onClick={() => dismiss(u.id)}
                                                        title="Dismiss without mapping"
                                                        className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded border border-border text-lg leading-none text-text-muted transition-colors hover:text-red-500 lg:h-auto lg:w-auto lg:border-0 lg:px-1"
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </section>

                {/* Current Aliases */}
                <section>
                    <h2 className="text-lg font-semibold text-text mb-4 border-b border-border pb-2">
                        Current Aliases{" "}
                        <span className="text-text-muted font-normal text-sm">
                            ({aliases.length})
                        </span>
                    </h2>

                    {aliases.length === 0 ? (
                        <p className="text-text-muted text-sm italic">
                            No aliases defined yet.
                        </p>
                    ) : (
                        <>
                            <div className="space-y-3 md:hidden">
                                {aliases.map((a) => (
                                    <article
                                        key={a.id}
                                        className="grid min-w-0 grid-cols-[1fr_auto] gap-3 rounded border border-border bg-white p-3"
                                    >
                                        <div className="min-w-0 space-y-3">
                                            <AliasValue
                                                label="Fantasy Name"
                                                value={a.player_name}
                                            />
                                            <AliasValue
                                                label="KTC Name"
                                                value={a.ktc_name || "(ignored)"}
                                                muted={!a.ktc_name}
                                            />
                                        </div>
                                        <button
                                            onClick={() => deleteAlias(a.id)}
                                            title="Delete alias"
                                            className="inline-flex h-10 w-10 items-center justify-center rounded border border-border text-lg leading-none text-text-muted transition-colors hover:text-red-500"
                                        >
                                            ×
                                        </button>
                                    </article>
                                ))}
                            </div>

                            <div className="hidden max-w-full overflow-x-auto rounded-lg border border-border bg-white md:block">
                                <table className="w-full min-w-[640px] text-sm border-collapse">
                                    <thead>
                                        <tr className="bg-surface border-b border-border text-left">
                                            <th className="py-2 px-3 font-semibold text-text-muted">
                                                Fantasy Name
                                            </th>
                                            <th className="py-2 px-3 font-semibold text-text-muted">
                                                KTC Name
                                            </th>
                                            <th className="py-2 px-3 w-12"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {aliases.map((a) => (
                                            <tr
                                                key={a.id}
                                                className="border-b border-border hover:bg-surface/60 transition-colors"
                                            >
                                                <td className="py-2 px-3 text-text break-words">
                                                    {a.player_name}
                                                </td>
                                                <td className="py-2 px-3 break-words">
                                                    {a.ktc_name ? (
                                                        <span className="text-text">{a.ktc_name}</span>
                                                    ) : (
                                                        <span className="text-text-muted italic">
                                                            (ignored)
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="py-2 px-3 text-right">
                                                    <button
                                                        onClick={() => deleteAlias(a.id)}
                                                        title="Delete alias"
                                                        className="text-text-muted hover:text-red-500 transition-colors text-lg leading-none"
                                                    >
                                                        ×
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </section>
            </div>
        </div>
    );
};

const AliasValue = ({ label, value, muted = false }) => (
    <div className="min-w-0">
        <p className="text-xs font-semibold uppercase text-text-muted">{label}</p>
        <p
            className={`mt-1 break-words text-sm ${
                muted ? "italic text-text-muted" : "text-text"
            }`}
        >
            {value}
        </p>
    </div>
);

export default AdminKTCMappingsPage;
